import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    Select,
    MenuItem,
    Button,
    FormHelperText,
} from '@mui/material';

interface GenerateReportModalProps {
    open: boolean;
    onClose: () => void;
    onGenerate: (month: number, year: number) => void;
    defaultMonth?: number;
    defaultYear?: number;
}

interface FormData {
    month: number | '';
    year: number | '';
}

const GenerateReportModal: React.FC<GenerateReportModalProps> = ({
    open,
    onClose,
    onGenerate,
    defaultMonth,
    defaultYear,
}) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors, isValid },
        watch,
    } = useForm<FormData>({
        defaultValues: {
            month: defaultMonth || '',
            year: defaultYear || '',
        },
        mode: 'onChange',
    });

    const onSubmit = (data: FormData) => {
        if (data.month !== '' && data.year !== '') {
            onGenerate(data.month as number, data.year as number);
        }
    };

    const handleClose = () => {
        reset({
            month: defaultMonth || '',
            year: defaultYear || '',
        });
        onClose();
    };

    const monthValue = watch('month');
    const yearValue = watch('year');

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
            <DialogTitle>Select Month & Year</DialogTitle>
            <DialogContent sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <FormControl fullWidth error={!!errors.month}>
                    <Controller
                        name="month"
                        control={control}
                        rules={{ required: 'Month is required' }}
                        render={({ field }) => (
                            <Select
                                {...field}
                                displayEmpty
                                renderValue={value =>
                                    value
                                        ? new Date(
                                              0,
                                              (value as number) - 1
                                          ).toLocaleString('default', {
                                              month: 'long',
                                          })
                                        : 'Select Month'
                                }
                            >
                                <MenuItem disabled value="">
                                    <em>Select Month</em>
                                </MenuItem>
                                {[...Array(12)].map((_, index) => (
                                    <MenuItem key={index + 1} value={index + 1}>
                                        {new Date(0, index).toLocaleString(
                                            'default',
                                            {
                                                month: 'long',
                                            }
                                        )}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                    {errors.month && (
                        <FormHelperText>{errors.month.message}</FormHelperText>
                    )}
                </FormControl>

                <FormControl fullWidth error={!!errors.year}>
                    <Controller
                        name="year"
                        control={control}
                        rules={{ required: 'Year is required' }}
                        render={({ field }) => (
                            <Select
                                {...field}
                                displayEmpty
                                renderValue={value =>
                                    value ? value.toString() : 'Select Year'
                                }
                            >
                                <MenuItem disabled value="">
                                    <em>Select Year</em>
                                </MenuItem>
                                {[2023, 2024, 2025, 2026, 2027].map(yr => (
                                    <MenuItem key={yr} value={yr}>
                                        {yr}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    />
                    {errors.year && (
                        <FormHelperText>{errors.year.message}</FormHelperText>
                    )}
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(onSubmit)}
                    disabled={!isValid || monthValue === '' || yearValue === ''}
                >
                    Generate
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GenerateReportModal;
